import { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { walletApi } from "../api/wallet";
import toast, { Toaster } from "react-hot-toast";
import Swal from "sweetalert2";

const WebSocketContext = createContext();

export const WebSocketProvider = ({ children }) => {
  const { user, tokens } = useAuth();
  const [socket, setSocket] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);

  
  const connectWebSocket = () => {
    console.log("Attempting WebSocket connection...");
    const ws = new WebSocket(
      `${import.meta.env.VITE_WS_URL}?token=${tokens.access.token}`
    );

    ws.onopen = () => {
      console.log("WebSocket connected successfully");
    };

    ws.onmessage = (event) => {
      console.log("WebSocket message received:", event.data);
      const data = JSON.parse(event.data);
      if (data.type === "PAYMENT_COMPLETED") {
        setPaymentStatus("completed");
        Swal.fire({
          icon: "success",
          title: "Payment completed successfully!",
          showConfirmButton: true,
          confirmButtonText: "OK",
          confirmButtonColor: "#0E64D1",
          background: "#1e3a8a",
          color: "#fff",
          fontFamily: "Poppins",
          fontSize: "16px",
          fontWeight: "bold",
          textAlign: "center",
          padding: "10px 20px",
          borderRadius: "10px",
        });
        refreshWalletBalance();
      }

      if (data.type === "LOAD_REQUEST_APPROVED") {
        toast.success("Load request approved successfully!");
        refreshWalletBalance();
      }

      if (data.type === "WITHDRAWAL_APPROVED") {
        toast.success("Withdrawal request approved successfully!");
        refreshWalletBalance();
      }

      if (data.type === "MANUAL_LOAD") {
        toast.success(`You received a load of P${data.amount} to your wallet!`);
        refreshWalletBalance();
      }

      if(data.type === "REQUESTER_WALLET_BALANCE"){
        refreshWalletBalance();
      }

      if(data.type === "COMMISSION_TO_WALLET"){
        refreshWalletBalance();
      }

      if(data.type === "WITHDRAWAL_CREATED"){
        refreshWalletBalance();
      }
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    ws.onclose = () => {
      console.log("WebSocket connection closed. Attempting to reconnect...");
      setTimeout(connectWebSocket, 3000); // Reconnect after 3 seconds
    };

    setSocket(ws);
    return ws;
  };

  useEffect(() => {
    if (user && tokens) {
      const ws = connectWebSocket();
      return () => {
        ws.close();
      };
    }
  }, [user, tokens]);

  const refreshWalletBalance = async () => {
    try {
      if (!tokens || !tokens.access || !tokens.access.token) {
        return;
      }
      const wallet = await walletApi.getWallet(tokens.access.token, user.id);
      
      // Check if we got a redirected response (session expired)
      if (wallet && wallet.redirected === true) {
        console.log('Wallet refresh detected expired token, redirect in progress');
        return null;
      }
      
      setWalletBalance(wallet.balance);
      return wallet.balance;
    } catch (error) {
      console.error("Error refreshing wallet:", error);
      return null; // Return null but don't rethrow to prevent UI issues
    }
  };

  // Function to reset payment status
  const resetPaymentStatus = () => {
    setPaymentStatus(null);
  };

  useEffect(() => {
    if (user && tokens) {
      refreshWalletBalance();
    }
  }, [user, tokens]);

  return (
    <WebSocketContext.Provider value={{ 
      paymentStatus, 
      refreshWalletBalance,
      walletBalance,
      resetPaymentStatus
    }}>
      {children}
      <Toaster position="bottom-center" reverseOrder={false} />
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => useContext(WebSocketContext);
